import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DxPopoverModule } from "devextreme-angular";
import { tagColor } from "../../project-hub.types";

interface TagData {
  id: number;
  name: string;
  color?: tagColor;
}

interface TagMap {
  [key: string]: TagData;
}

@Component({
    selector: "app-spark-tags-overflow",
    imports: [CommonModule, DxPopoverModule],
    templateUrl: "./spark-tags-overflow.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./spark-tags-overflow.component.css"]
})
export class SparkTagsOverflowComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() tagIds: string[] = [];
  @Input() tags: TagMap = {};
  @Input() maxWidth?: number; // Optional max width override
  @Input() alignRight = false;

  @ViewChild("tagsContainer", { static: false }) tagsContainer!: ElementRef;

  visibleTags: TagData[] = [];
  hiddenTags: TagData[] = [];
  showTooltip = false;
  private resizeObserver?: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.calculateVisibleTags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tagIds"] || changes["tags"]) {
      this.calculateVisibleTags();
    }
  }

  ngAfterViewInit(): void {
    // Initial calculation with slight delay to ensure DOM is ready
    setTimeout(() => {
      this.calculateVisibleTags();
    }, 0);

    // Set up resize observer to recalculate when container size changes
    if (this.tagsContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        // Debounce resize calculations
        setTimeout(() => {
          this.calculateVisibleTags();
        }, 50);
      });
      this.resizeObserver.observe(this.tagsContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private calculateVisibleTags(): void {
    if (!this.tagsContainer || !this.tagIds.length) {
      // If container not ready or no tags, show all tags for now
      this.visibleTags = this.tagIds.map((id) => this.tags[id]).filter(Boolean);
      this.hiddenTags = [];
      return;
    }

    const containerElement = this.tagsContainer.nativeElement;
    const containerWidth = this.maxWidth || containerElement.offsetWidth;

    // If container has no width yet, show all tags
    if (containerWidth <= 0) {
      this.visibleTags = this.tagIds.map((id) => this.tags[id]).filter(Boolean);
      this.hiddenTags = [];
      return;
    }

    const ellipsisWidth = 70; // Approximate width of "+X more" button
    const availableWidthWithEllipsis = containerWidth - ellipsisWidth;

    let currentWidth = 0;
    this.visibleTags = [];
    this.hiddenTags = [];

    // Always show at least one tag if available
    for (const tagId of this.tagIds) {
      const tagData = this.tags[tagId];
      if (!tagData) continue;

      const tagWidth = this.estimateTagWidth(tagData.name);

      // First tag is always visible, subsequent tags check available width
      if (
        this.visibleTags.length === 0 ||
        currentWidth + tagWidth <= availableWidthWithEllipsis
      ) {
        this.visibleTags.push(tagData);
        currentWidth += tagWidth;
      } else {
        this.hiddenTags.push(tagData);
      }
    }

    // If we only have one hidden tag and it would fit without ellipsis, show it
    if (this.hiddenTags.length === 1) {
      const lastHiddenTag = this.hiddenTags[0];
      const lastHiddenWidth = this.estimateTagWidth(lastHiddenTag.name);

      if (currentWidth + lastHiddenWidth <= containerWidth) {
        this.visibleTags.push(lastHiddenTag);
        this.hiddenTags = [];
      }
    }

    this.cdr.detectChanges();
  }

  private estimateTagWidth(tagName: string): number {
    // Create a temporary element to measure tag width
    const tempElement = document.createElement("span");
    tempElement.className = "spark-tag";
    tempElement.style.visibility = "hidden";
    tempElement.style.position = "absolute";
    tempElement.style.whiteSpace = "nowrap";
    tempElement.textContent = tagName;

    document.body.appendChild(tempElement);
    const width = tempElement.offsetWidth + 4; // Add gap
    document.body.removeChild(tempElement);

    return width;
  }

  onEllipsisClick(): void {
    this.showTooltip = !this.showTooltip;
  }

  onPopoverHiding(): void {
    this.showTooltip = false;
  }

  get hasHiddenTags(): boolean {
    return this.hiddenTags.length > 0;
  }

  get hiddenTagCount(): number {
    return this.hiddenTags.length;
  }
}
